import React, { useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Download } from "lucide-react";

// Prohlížeč fotek přes celou obrazovku (8.9.2026).
//
// Proč: fotky u hlášení uživatelů se otevíraly jako odkaz na soubor v nové kartě.
// Na telefonu tím člověk vypadl z aplikace do holé stránky s obrázkem, kde není
// žádné zpět ani křížek, a zpátky se dostal jen přes správu karet prohlížeče.
//
// Ovládání: křížek nebo klik mimo fotku zavře, šipky (na obrazovce, na klávesnici
// i tahem prstem) listují. Stažení otevře originál v nové kartě pro toho, kdo ho
// opravdu chce.
export default function FotkaLightbox({ fotky = [], index = 0, onClose, onIndex }) {
  const dotyk = useRef(null);
  const pocet = fotky.length;
  const aktualni = fotky[index];

  const dalsi = useCallback(() => { if (pocet > 1) onIndex?.((index + 1) % pocet); }, [index, pocet, onIndex]);
  const predchozi = useCallback(() => { if (pocet > 1) onIndex?.((index - 1 + pocet) % pocet); }, [index, pocet, onIndex]);

  useEffect(() => {
    const naKlavesu = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "ArrowRight") dalsi();
      else if (e.key === "ArrowLeft") predchozi();
    };
    window.addEventListener("keydown", naKlavesu);
    return () => window.removeEventListener("keydown", naKlavesu);
  }, [onClose, dalsi, predchozi]);

  if (!aktualni) return null;

  const zacatekTahu = (e) => { dotyk.current = e.touches?.[0]?.clientX ?? null; };
  const konecTahu = (e) => {
    if (dotyk.current === null) return;
    const rozdil = (e.changedTouches?.[0]?.clientX ?? dotyk.current) - dotyk.current;
    dotyk.current = null;
    if (Math.abs(rozdil) < 50) return;
    if (rozdil < 0) dalsi(); else predchozi();
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] flex flex-col bg-[#0B0710]" onClick={onClose}>
      <div className="flex items-center gap-2 px-3 py-3" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={onClose} aria-label="Zavřít" title="Zavřít (Esc)"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white hover:bg-white/20">
          <X className="w-5 h-5" />
        </button>
        <span className="min-w-0 flex-1 text-center text-[13px] font-semibold text-white/80">
          {pocet > 1 ? `Fotka ${index + 1} ze ${pocet}` : "Fotka"}
        </span>
        <a href={aktualni} target="_blank" rel="noreferrer" aria-label="Otevřít originál" title="Otevřít originál v nové kartě"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white hover:bg-white/20">
          <Download className="w-5 h-5" />
        </a>
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center px-3"
        onTouchStart={zacatekTahu} onTouchEnd={konecTahu}>
        <img src={aktualni} alt={`Fotka ${index + 1}`} onClick={(e) => e.stopPropagation()}
          className="max-h-full max-w-full rounded-xl object-contain" />
      </div>

      {pocet > 1 ? (
        <div className="safe-bottom flex items-center justify-between px-3 py-4" onClick={(e) => e.stopPropagation()}>
          <button type="button" onClick={predchozi} aria-label="Předchozí fotka"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-1.5">
            {fotky.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full ${i === index ? "w-4 bg-white" : "w-1.5 bg-white/40"}`} />
            ))}
          </div>
          <button type="button" onClick={dalsi} aria-label="Další fotka"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      ) : (
        <div className="safe-bottom py-4" />
      )}
    </div>,
    document.body
  );
}
